/**
 * CourtConfirmationModal - Modal para confirmar/iniciar sesión en cancha
 * 
 * Flujo simplificado:
 * 1. Usuario SIN sesión → 2 opciones: "Iniciar sesión" o "Verificar OTP"
 * 2. Usuario CON sesión → Confirmar activación de cancha
 */
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MapPin, Loader2, Play, CheckCircle2, Phone, 
  ArrowRight, X, LogIn, UserPlus, KeyRound
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { InputOTP, InputOTPGroup, InputOTPSlot } from './ui/input-otp';
import { authAPI } from '../services/api';
import { useAuthStore } from '../store';
import { toast } from 'sonner';

const normalizePhone = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  return digits ? `+${digits}` : '';
};

export default function CourtConfirmationModal({ 
  isOpen, 
  onClose, 
  clubInfo, 
  onConfirm,
  isLoading = false 
}) {
  const { user, isAuthenticated, token, login } = useAuthStore();
  
  // Steps: 'checking' | 'choose' | 'login' | 'otp-phone' | 'otp-verify' | 'confirm'
  const [step, setStep] = useState('checking');
  
  // Login form
  const [loginPhone, setLoginPhone] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  
  // OTP flow
  const [otpPhone, setOtpPhone] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Check auth state when modal opens
  useEffect(() => {
    if (!isOpen) {
      // Reset state when modal closes
      setStep('checking');
      setLoginPhone('');
      setLoginPassword('');
      setOtpPhone('');
      setOtpCode('');
      return;
    }
    
    // Small delay to ensure localStorage is read
    const checkAuth = () => {
      // Check both Zustand store and localStorage directly
      let hasValidSession = false;
      
      // Check Zustand store
      if (isAuthenticated && token && user) {
        hasValidSession = true;
        console.log('[CourtModal] User authenticated via Zustand store');
      }
      
      // Also check localStorage directly as fallback
      if (!hasValidSession) {
        try {
          const stored = localStorage.getItem('clipper-auth');
          if (stored) {
            const parsed = JSON.parse(stored);
            if (parsed?.state?.token && parsed?.state?.user) {
              hasValidSession = true;
              console.log('[CourtModal] User authenticated via localStorage');
              // Sync to store if needed
              if (!token && parsed.state.token) {
                login(parsed.state.user, parsed.state.token);
              }
            }
          }
        } catch (e) {
          console.log('[CourtModal] Error checking localStorage:', e);
        }
      }
      
      console.log('[CourtModal] Auth check result:', { hasValidSession, isAuthenticated, hasToken: !!token });
      
      if (hasValidSession) {
        setStep('confirm');
      } else {
        setStep('choose');
      }
    };
    
    // Small timeout to let hydration complete
    setTimeout(checkAuth, 100);
  }, [isOpen, isAuthenticated, token, user, login]);

  // Handle login with phone + password
  const handleLogin = async () => {
    const phone = normalizePhone(loginPhone);
    if (!phone || phone.length < 10) {
      toast.error('Ingresa un número de celular válido');
      return;
    }
    if (!loginPassword) {
      toast.error('Ingresa tu contraseña');
      return;
    }

    setLoginLoading(true);
    try {
      const response = await authAPI.login(phone, loginPassword);
      login(response.data.user, response.data.token);
      toast.success('¡Sesión iniciada!');
      setStep('confirm');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Credenciales inválidas';
      toast.error(detail);
    } finally {
      setLoginLoading(false);
    }
  };

  // Handle send OTP
  const handleSendOtp = async () => {
    const phone = normalizePhone(otpPhone);
    if (!phone || phone.length < 10) {
      toast.error('Ingresa un número de celular válido');
      return;
    }

    setOtpLoading(true);
    try {
      const response = await authAPI.startPlayerOTP(phone);
      setOtpPhone(phone);
      setStep('otp-verify');
      
      // Start resend cooldown
      const cooldown = response.data?.resend_available_in || 30;
      setResendCooldown(cooldown);
      const interval = setInterval(() => {
        setResendCooldown(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      toast.success('Código enviado por SMS');
    } catch (err) {
      const detail = err.response?.data?.detail || 'No se pudo enviar el código';
      toast.error(detail);
      
      // If user already has account, suggest login
      if (detail.includes('ya tiene cuenta')) {
        toast.info('Usa "Iniciar sesión" con tu celular y contraseña');
        setLoginPhone(otpPhone);
        setStep('login');
      }
    } finally {
      setOtpLoading(false);
    }
  };

  // Handle verify OTP
  const handleVerifyOtp = async () => {
    if (otpCode.length !== 6) {
      toast.error('Ingresa el código de 6 dígitos');
      return;
    }

    setOtpLoading(true);
    try {
      const response = await authAPI.verifyPlayerOTP(otpPhone, otpCode);
      login(response.data.user, response.data.token);
      toast.success('¡Celular verificado!');
      setStep('confirm');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Código inválido o expirado');
    } finally {
      setOtpLoading(false);
    }
  };

  if (!isOpen || !clubInfo) return null;

  return (
    <AnimatePresence>
      <motion.div 
        initial={{ opacity: 0 }} 
        animate={{ opacity: 1 }} 
        exit={{ opacity: 0 }} 
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4"
        onClick={(e) => e.target === e.currentTarget && onClose()}
        data-testid="court-confirmation-overlay"
      >
        <motion.div 
          initial={{ scale: 0.95, opacity: 0, y: 20 }} 
          animate={{ scale: 1, opacity: 1, y: 0 }} 
          exit={{ scale: 0.95, opacity: 0, y: 20 }}
          className="w-full max-w-md rounded-2xl bg-[#0D121D] border border-white/10 overflow-hidden shadow-2xl"
          data-testid="court-confirmation-modal"
        >
          {/* Header with club/court info */}
          <div className="bg-gradient-to-br from-[#131B2A] to-[#0D121D] p-6 text-white border-b border-white/10">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[#BBFF00] text-sm font-mono tracking-wider uppercase">
                  {step === 'checking' ? 'Verificando...' :
                   step === 'choose' ? 'Identificación requerida' :
                   step === 'login' ? 'Iniciar sesión' :
                   step === 'otp-phone' ? 'Verificar celular' :
                   step === 'otp-verify' ? 'Ingresa el código' :
                   '¿Activar cancha?'}
                </p>
                <h2 className="text-2xl font-display font-bold mt-1 text-slate-50">{clubInfo.club?.name}</h2>
                <p className="text-slate-400 mt-1 flex items-center gap-1">
                  <Play className="h-4 w-4 text-[#BBFF00]" />
                  Cancha {clubInfo.court?.court_number || clubInfo.court?.number || clubInfo.court?.name}
                </p>
              </div>
              <button 
                onClick={onClose}
                className="p-1.5 rounded-full hover:bg-white/10 transition-colors text-slate-400 hover:text-white"
                data-testid="court-modal-close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            {clubInfo.club?.location?.address && (
              <p className="text-slate-500 text-sm mt-3 flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                {clubInfo.club.location.address}
              </p>
            )}
          </div>

          {/* Content */}
          <div className="p-6 bg-[#0D121D]">
            
            {/* STEP: Checking auth */}
            {step === 'checking' && (
              <div className="flex items-center justify-center py-8" data-testid="modal-checking">
                <Loader2 className="h-8 w-8 animate-spin text-[#BBFF00]" />
              </div>
            )}
            
            {/* STEP: Choose - Login or OTP */}
            {step === 'choose' && (
              <div className="space-y-4" data-testid="modal-choose-step">
                <p className="text-slate-400 text-sm text-center mb-6">
                  Para activar esta cancha, necesitas identificarte:
                </p>
                
                <Button 
                  onClick={() => setStep('login')}
                  className="w-full h-14 bg-[#BBFF00] hover:bg-[#A2DC00] text-[#070A0F] font-bold shadow-[0_0_20px_rgba(187,255,0,0.3)]"
                  data-testid="choose-login-btn"
                >
                  <LogIn className="h-5 w-5 mr-3" />
                  Iniciar sesión
                  <span className="ml-auto text-xs opacity-70">Celular + contraseña</span>
                </Button>
                
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-white/10" />
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="bg-[#0D121D] px-2 text-slate-500">o</span>
                  </div>
                </div>
                
                <Button 
                  onClick={() => setStep('otp-phone')}
                  variant="outline"
                  className="w-full h-14 border-white/20 text-white hover:bg-white/5"
                  data-testid="choose-otp-btn"
                >
                  <UserPlus className="h-5 w-5 mr-3" />
                  Soy nuevo / Verificar OTP
                  <span className="ml-auto text-xs opacity-70">Código SMS</span>
                </Button>
              </div>
            )}
            
            {/* STEP: Login with phone + password */}
            {step === 'login' && (
              <div className="space-y-4" data-testid="modal-login-step">
                <div className="space-y-2">
                  <Label htmlFor="login-phone" className="text-slate-300">Número de celular</Label>
                  <Input
                    id="login-phone"
                    type="tel"
                    value={loginPhone}
                    onChange={(e) => setLoginPhone(e.target.value)}
                    placeholder="+526441234567"
                    className="h-12 bg-[#131B2A] border-white/10 text-slate-100 placeholder:text-slate-500 focus:border-[#BBFF00]"
                    data-testid="login-phone-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="login-password" className="text-slate-300">Contraseña</Label>
                  <Input
                    id="login-password"
                    type="password"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    placeholder="Tu contraseña"
                    className="h-12 bg-[#131B2A] border-white/10 text-slate-100 placeholder:text-slate-500 focus:border-[#BBFF00]"
                    data-testid="login-password-input"
                    onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                  />
                </div>

                <Button 
                  onClick={handleLogin} 
                  disabled={loginLoading}
                  className="w-full h-12 bg-[#BBFF00] hover:bg-[#A2DC00] text-[#070A0F] font-bold shadow-[0_0_20px_rgba(187,255,0,0.3)]"
                  data-testid="login-submit-btn"
                >
                  {loginLoading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <>
                      <KeyRound className="h-5 w-5 mr-2" />
                      Iniciar sesión
                    </>
                  )}
                </Button>
                
                <button 
                  onClick={() => setStep('choose')}
                  className="w-full text-sm text-slate-500 hover:text-slate-300 py-2"
                  data-testid="login-back-btn"
                >
                  ← Volver
                </button>
              </div>
            )}
            
            {/* STEP: OTP - Enter phone */}
            {step === 'otp-phone' && (
              <div className="space-y-4" data-testid="modal-otp-phone-step">
                <p className="text-slate-400 text-sm">
                  Te enviaremos un código SMS para verificar tu celular.
                </p>
                
                <div className="space-y-2">
                  <Label htmlFor="otp-phone" className="text-slate-300">Número de celular</Label>
                  <Input
                    id="otp-phone"
                    type="tel"
                    value={otpPhone}
                    onChange={(e) => setOtpPhone(e.target.value)}
                    placeholder="+526441234567"
                    className="h-12 bg-[#131B2A] border-white/10 text-slate-100 placeholder:text-slate-500 focus:border-[#BBFF00]"
                    data-testid="otp-phone-input"
                  />
                  <p className="text-xs text-slate-500">
                    Incluye código de país (ej: +52 para México)
                  </p>
                </div>

                <Button 
                  onClick={handleSendOtp} 
                  disabled={otpLoading}
                  className="w-full h-12 bg-[#BBFF00] hover:bg-[#A2DC00] text-[#070A0F] font-bold shadow-[0_0_20px_rgba(187,255,0,0.3)]"
                  data-testid="otp-send-btn"
                >
                  {otpLoading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <>
                      <Phone className="h-5 w-5 mr-2" />
                      Enviar código SMS
                    </>
                  )}
                </Button>
                
                <button 
                  onClick={() => setStep('choose')}
                  className="w-full text-sm text-slate-500 hover:text-slate-300 py-2"
                  data-testid="otp-back-btn"
                >
                  ← Volver
                </button>
              </div>
            )}
            
            {/* STEP: OTP - Verify code */}
            {step === 'otp-verify' && (
              <div className="space-y-4" data-testid="modal-otp-verify-step">
                <div className="text-center">
                  <p className="text-slate-400 text-sm">Código enviado a</p>
                  <p className="font-semibold text-lg text-slate-100">{otpPhone}</p>
                </div>

                <div className="flex justify-center">
                  <InputOTP 
                    maxLength={6} 
                    value={otpCode} 
                    onChange={setOtpCode}
                    data-testid="otp-code-input"
                  >
                    <InputOTPGroup>
                      {[0,1,2,3,4,5].map(i => (
                        <InputOTPSlot key={i} index={i} className="bg-[#131B2A] border-white/10 text-slate-100" />
                      ))}
                    </InputOTPGroup>
                  </InputOTP>
                </div>

                <Button 
                  onClick={handleVerifyOtp} 
                  disabled={otpLoading || otpCode.length !== 6}
                  className="w-full h-12 bg-[#BBFF00] hover:bg-[#A2DC00] text-[#070A0F] font-bold shadow-[0_0_20px_rgba(187,255,0,0.3)] disabled:opacity-50"
                  data-testid="otp-verify-btn"
                >
                  {otpLoading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <>
                      <CheckCircle2 className="h-5 w-5 mr-2" />
                      Verificar código
                    </>
                  )}
                </Button>

                <div className="flex items-center justify-between text-sm">
                  <button 
                    onClick={() => setStep('otp-phone')}
                    className="text-slate-500 hover:text-slate-300"
                    data-testid="otp-change-phone-btn"
                  >
                    Cambiar número
                  </button>
                  
                  <button 
                    onClick={handleSendOtp}
                    disabled={resendCooldown > 0 || otpLoading}
                    className="text-[#BBFF00] hover:text-[#A2DC00] disabled:text-slate-600"
                    data-testid="otp-resend-btn"
                  >
                    {resendCooldown > 0 ? `Reenviar (${resendCooldown}s)` : 'Reenviar código'}
                  </button>
                </div>
              </div>
            )}
            
            {/* STEP: Confirm - User is authenticated */}
            {step === 'confirm' && (
              <div className="space-y-4" data-testid="modal-confirm-step">
                <div className="bg-[#BBFF00]/10 rounded-xl p-4 border border-[#BBFF00]/30">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-[#BBFF00] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-slate-100">¡Listo para grabar!</p>
                      <p className="text-sm text-slate-400 mt-1">
                        Tus clips se guardarán automáticamente durante las próximas 8 horas.
                      </p>
                    </div>
                  </div>
                </div>

                <Button 
                  onClick={onConfirm}
                  disabled={isLoading}
                  className="w-full h-14 text-lg bg-[#BBFF00] hover:bg-[#A2DC00] text-[#070A0F] font-bold shadow-[0_0_25px_rgba(187,255,0,0.35)]"
                  data-testid="confirm-activate-btn"
                >
                  {isLoading ? (
                    <Loader2 className="h-6 w-6 animate-spin" />
                  ) : (
                    <>
                      <ArrowRight className="h-6 w-6 mr-2" />
                      Activar cancha
                    </>
                  )}
                </Button>

                <button 
                  onClick={onClose}
                  className="w-full text-sm text-slate-500 hover:text-slate-300 py-2"
                  data-testid="confirm-cancel-btn"
                >
                  Cancelar
                </button>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
